import {
  ActionError,
  ApiError,
  ApiErrorWithStatus,
  ErrorFallbackError,
  ValidationError,
  ValidationErrorType,
} from "@/ErrorHandling/types";
import { TranslationFn } from "@/libs/shared";
import * as R from "remeda";

/**
 * Extract status codes or error codes from the top level of an error object
 */
export function extractErrorCodes(error: ErrorFallbackError | ErrorFallbackError[] | undefined): (number | string)[] {
  if (!error) {
    return [];
  }

  if (Array.isArray(error)) {
    return error.flatMap(e => extractErrorCodes(e));
  }

  if (R.isPlainObject(error)) {
    if ("error" in error && error.error && R.isPlainObject(error.error)) {
      return extractErrorCodes(error.error as ErrorFallbackError);
    }
    if ("errors" in error && Array.isArray(error.errors)) {
      return error.errors.flatMap(e => extractErrorCodes(e));
    }
  }

  const codes: (number | string)[] = [];

  if ("status" in error && R.isNonNullish(error.status)) {
    codes.push(error.status);
  }
  if ("code" in error && R.isNonNullish(error.code)) {
    codes.push(error.code);
  }
  if ("statusCode" in error && R.isNonNullish(error.statusCode)) {
    codes.push(error.statusCode as number);
  }
  if ("originalStatus" in error && R.isNonNullish(error.originalStatus)) {
    codes.push(error.originalStatus);
  }

  return codes;
}

/**
 * Check if an error has a specific code or status at the top level
 */
function hasCodeOrStatus(error: ErrorFallbackError | ErrorFallbackError[] | undefined, code: number | string): boolean {
  const codes = extractErrorCodes(error);
  return codes.includes(code);
}

const API_ERROR_TYPES = ["validation_error", "client_error"];

/**
 * Validate whether all errors in a list are ApiErrors
 * @param errors
 */
export function isApiError(error: ApiError | any): error is ApiErrorWithStatus {
  return API_ERROR_TYPES.includes(error?.data?.type);
}

/**
 * Note: going forward we can expand this for better error messages.
 * Should create a separate error handling for situation where forms are present.
 * @param errors
 */
export function parseValidationError({ errors }: ValidationError): string {
  return errors.map(error => error.detail).join("\n");
}

export function parseApiError({ data }: ApiErrorWithStatus): string {
  switch (data.type) {
    case "validation_error":
      return parseValidationError(data);
    case "client_error":
      return data.errors.map(e => e.detail).join(", ");
  }
}

export function extractValidationError(error: ErrorFallbackError): ValidationErrorType[] | null {
  if (isApiError(error) && error.data.type === "validation_error") {
    return error.data.errors;
  }
  return null;
}

export interface ParsedError {
  msg: string;
  refetchable: boolean;
  error_title?: string;
  cancel_button?: string;
}

export function parseError(error: ErrorFallbackError, t: TranslationFn, isSuperAdmin: boolean = false): ParsedError {
  if (isApiError(error)) {
    // For now we are assuming refetchable false for now, later
    // we can decide based on the error code.
    return { msg: parseApiError(error), refetchable: false };
  }

  const customError = hasCodeOrStatus(error, "CUSTOM_ERROR") ? (error as ActionError) : undefined;
  if (customError) {
    // While it's unlikely that the msg won't be a string type, we can't guarantee it due to all
    // kinds of different error handling in the app. This acts as a fail-safe
    return {
      msg: typeof customError.msg === "string" ? customError.msg : t("errors.unknown"),
      refetchable: customError.refetchable || false,
      error_title: customError.error_title,
      cancel_button: customError.cancel_button,
    };
  }

  const tenantNotProvided = t("general.tenant_not_provided_for_current_user");
  if (hasCodeOrStatus(error, 400) && !isSuperAdmin && "data" in error && error.data[0] === tenantNotProvided) {
    return { msg: isSuperAdmin ? t("errors.superadmin_no_tenant") : t("errors.no_tenant"), refetchable: false };
  }
  if (hasCodeOrStatus(error, 409) && "data" in error && error.data?.detail === "User must have a tenant.") {
    return { msg: t("errors.no_tenant"), refetchable: false };
  }

  if (hasCodeOrStatus(error, 403)) {
    return { msg: error.userAction ? t("errors.403_action") : t("errors.403"), refetchable: false };
  }
  if (hasCodeOrStatus(error, 404)) {
    return { msg: error.userAction ? t("errors.404_action") : t("errors.404"), refetchable: false };
  }
  if (hasCodeOrStatus(error, 500)) {
    return { msg: t("errors.500"), refetchable: true };
  }
  if (hasCodeOrStatus(error, 502)) {
    return { msg: t("errors.502"), refetchable: true };
  }
  if (hasCodeOrStatus(error, "FETCH_ERROR")) {
    return { msg: t("errors.fetch_error"), refetchable: true };
  }

  return {
    msg: t("errors.unknown"),
    refetchable: true,
  };
}

export function extractAPIErrorMessage(error: any): string {
  const FALLBACK_ERROR_MESSAGE = "Unknown error";
  const FALLBACK_ERROR_CODE = "UNKNOWN";

  if (error instanceof Error) return error.message;

  const statusCodes = extractErrorCodes(error);
  const errorStatusCode = statusCodes[0] || FALLBACK_ERROR_CODE;
  const errorMessage = findFirstStringInError(error) || FALLBACK_ERROR_MESSAGE;

  return `APIError ${errorStatusCode} - ${errorMessage}`;
}

/**
 * Recursively searches through an object to find the first meaningful string value
 * Priority is given to common error message fields like "message", "detail", "error", and "description".
 * @param errorObj The object to search through
 * @param visited Set to prevent circular reference issues
 * @returns The first meaningful string found or null
 */
function findFirstStringInError(errorObj: any, visited = new Set<unknown>()): string | null {
  if (R.isNullish(errorObj)) return null;
  if (R.isString(errorObj)) return errorObj || null; // Empty strings are treated as null

  // Prevent circular references
  if (R.isObjectType(errorObj)) {
    if (visited.has(errorObj)) return null;
    visited.add(errorObj);
  }

  if (R.isArray(errorObj)) {
    for (const item of errorObj) {
      const result = findFirstStringInError(item, visited);
      if (result) return result;
    }
    return null;
  }

  // Handle objects
  if (R.isPlainObject(errorObj)) {
    // Check priority fields first
    // We prioritise data because that's the field FetchBaseQuery puts the server response in
    const priorityFields = ["data", "error", "detail", "message", "description"];
    for (const field of priorityFields) {
      if (errorObj[field]) {
        const result = findFirstStringInError(errorObj[field], visited);
        if (result) return result;
      }
    }

    // Check all other fields
    for (const key in errorObj) {
      if (priorityFields.includes(key)) continue; // Skip already checked fields

      const result = findFirstStringInError(errorObj[key], visited);
      if (result) return result;
    }
  }

  return null;
}

export function createRichError(originalError: any, errorInfo: React.ErrorInfo, t: TranslationFn): Error {
  const error = new Error(extractAPIErrorMessage(originalError));

  // Remove refetch property if it exists
  const { refetch, ...errorFields } = originalError;

  error.name = "APIError";
  error.cause = errorFields;
  error.stack = errorInfo.componentStack || originalError.stack || undefined;

  return error;
}

/**
 * Type guard to check if an error has the API error structure with errors array
 */
export function isMultipleAPIErrors(error: unknown): error is { errors: ErrorFallbackError[] } {
  return !!error && typeof error === "object" && "errors" in error && Array.isArray((error as any).errors);
}

/**
 * Type guard to check if an error has the API error structure with errors array
 */
export function isSingleAPIError(error: unknown): error is { error: ErrorFallbackError } {
  return (
    !!error &&
    typeof error === "object" &&
    "error" in error &&
    !Array.isArray((error as any).error) &&
    !(error.error instanceof Error)
  );
}
